import Vue from 'vue'
import App from './App.vue'
import createRouter from './router'
import createStore from './store'
const imageFallback = require('@/assets/default/image-fallback.png')

import VueLazyload from 'vue-lazyload'
import global from '@/utils/global'
import { initializeApp } from 'firebase/app'
import { getAnalytics, logEvent, isSupported } from 'firebase/analytics'
import VueMeta from 'vue-meta'
import firebaseConfig from '../firebaseConfig'
import './plugins/element.js'
import { createStatsExtra, ensureStatsPvId, reportAndroidStatsEvent, withStatsPvId } from '@/utils/statsTelemetry'

/**
 * Facebook Pixel 配置
 * 域名映射到像素ID
 */
const FB_PIXEL_CONFIG = {
  'elegant.nameailist.com': '961928386286137',
  'nature.nameailist.com': '1565072044577193'
}

/**
 * 设置 StartTrial 事件计时器
 * 用户停留10秒后触发
 */
function setupStartTrialTimer() {
  if (typeof window === 'undefined') return

  const START_TRIAL_DELAY = 10000 // 10秒
  let timerStarted = false
  let timerId = null

  const startTimer = () => {
    if (timerStarted) return
    timerStarted = true

    timerId = setTimeout(() => {
      if (window.fbq) {
        window.fbq('track', 'StartTrial')
        console.log('[Facebook Pixel] StartTrial event fired after 10s')
      }
    }, START_TRIAL_DELAY)
  }

  const clearTimer = () => {
    if (timerId) {
      clearTimeout(timerId)
      timerId = null
      timerStarted = false
    }
  }

  // 页面可见性变化时处理
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearTimer()
    } else {
      startTimer()
    }
  })

  // 页面加载完成后启动计时器
  if (document.readyState === 'complete') {
    startTimer()
  } else {
    window.addEventListener('load', startTimer)
  }
}

// 事件队列管理器
class EventQueueManager {
	constructor() {
		this.queue = []
		this.initialized = false
	}

	add(event) {
		this.initialized ? this.processEvent(event) : this.queue.push(event)
	}

	processEvent(event) {
		const { type, ...data } = event
		if (type === 'log') {
			Vue.prototype.$logEvent(data.event, data.params)
		} else if (type === 'track') {
			Vue.prototype.$eventrack(data.msg, data.method, data.map)
		}
	}

	processQueue() {
		this.initialized = true
		while (this.queue.length > 0) {
			this.processEvent(this.queue.shift())
		}
	}
}

const eventQueueManager = new EventQueueManager()

Vue.prototype.$global = global

// 路由切换后重新合并 meta，避免异步数据更新后 head 未刷新
Vue.use(VueMeta, {
	refreshOnceOnNavigation: true,
})

Vue.use(VueLazyload, {
	preLoad: 1.3,
	error: imageFallback,
	loading: imageFallback,
	attempt: 1,
})

// 初始化默认事件追踪方法
function initializeDefaultTracking() {
	Vue.prototype.$logEvent = (event, params = {}) => {
		eventQueueManager.add({ type: 'log', event, params })
	}

	Vue.prototype.$eventrack = (msg, method, map = {}) => {
		eventQueueManager.add({ type: 'track', msg, method, map })
	}
}

// Firebase 初始化与配置
async function initializeFirebaseAnalytics() {
	try {
		const isAnalyticsSupported = await isSupported()
		if (!isAnalyticsSupported) {
			console.warn('Firebase Analytics is not supported in this environment')
			return null
		}

		const fconfig = window?.__INITIAL_STATE__?.domainConfig?.firebase || firebaseConfig
		const app = initializeApp(fconfig)
		return getAnalytics(app)
	} catch (error) {
		console.error('Failed to initialize Firebase:', error)
		return null
	}
}

// 设置生产环境分析
async function setupAnalytics() {
	const analytics = await initializeFirebaseAnalytics()
	if (!analytics) return

	reportCurrentPageView(analytics)

	// 更新追踪方法
	Vue.prototype.$logEvent = (event, params = {}) => {
		const eventParams = withStatsPvId(params)
		console.log(`[Analytics] ${event}`, eventParams)
		logEvent(analytics, event, eventParams)
	}

	Vue.prototype.$eventrack = (msg, method, map = {}) => {
		const params = {
			timestamp: new Date().toISOString(),
			message: msg,
			method,
			...map,
		}
		const eventParams = withStatsPvId(params)
		console.log(`[Analytics] ${msg}`, eventParams)
		logEvent(analytics, msg, eventParams)
	}

	// 处理队列中的事件
	eventQueueManager.processQueue()
}

function getCurrentPageParams(statsPvId) {
	return {
		stats_pv_id: statsPvId,
		path: window.location.pathname,
		search: window.location.search,
		title: document.title,
		referrer: document.referrer || '',
		timestamp: Date.now(),
	}
}

function reportCurrentPageView(analytics) {
	if (typeof window === 'undefined') return

	const statsPvId = ensureStatsPvId()
	const pageParams = getCurrentPageParams(statsPvId)
	logEvent(analytics, 'in_page', pageParams)
	reportAndroidStatsEvent('in_page', window.location.href, createStatsExtra(statsPvId, pageParams))
}

// 设置原生接口
function setupNativeInterfaces() {
	Vue.prototype.$adClick = (message) => {
		if (typeof JSCallAndroid !== 'undefined' && typeof JSCallAndroid.adClick === 'function') {
			console.log(message)
			JSCallAndroid.adClick(message)
		} else {
			console.log(`Ad Click (no native): ${message}`)
		}
	}

	Vue.prototype.$adPosition = (message) => {
		if (typeof JSCallAndroid !== 'undefined' && typeof JSCallAndroid.adPosition === 'function') {
			console.log(message)
			JSCallAndroid.adPosition(message)
		} else {
			console.log(`Ad Position (no native): ${message}`)
		}
	}

	Vue.prototype.$statsEvent = (eventName, extraParams = {}) => {
		const statsPvId = ensureStatsPvId()
		const url = window.location.href
		return reportAndroidStatsEvent(eventName, url, createStatsExtra(statsPvId, extraParams))
	}

	// Facebook ViewContent 事件追踪
	Vue.prototype.$fbViewContent = (contentData) => {
		const pixelId = window.__INITIAL_STATE__?.hostInject?.facebook?.pixelId
		if (!pixelId) {
			console.log('[Facebook Pixel] No pixelId configured')
			return
		}
		if (typeof fbq !== 'function') {
			console.log('[Facebook Pixel] fbq not available')
			return
		}
		console.log('[Facebook Pixel] Tracking ViewContent:', contentData)
		fbq('track', 'ViewContent', contentData)
	}
}

// 初始化 Facebook Pixel（标准 queue snippet）
function initializeFacebookPixel(pixelId) {
  if (!pixelId || typeof window === 'undefined') return

  // 标准 FB Pixel queue snippet
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');

  fbq('init', pixelId)
  fbq('track', 'PageView')
  console.log('[Facebook Pixel] Initialized with ID:', pixelId)

  // 设置停留10秒后触发 StartTrial 事件
  setupStartTrialTimer()
}

// 初始化应用
if (typeof window !== 'undefined') {
	initializeDefaultTracking()
	setupAnalytics()
	setupNativeInterfaces()

	// 初始化 Facebook Pixel
	const pixelId = window.__INITIAL_STATE__?.hostInject?.facebook?.pixelId || FB_PIXEL_CONFIG[window.location.hostname]
	if (pixelId) {
		initializeFacebookPixel(pixelId)
	}
} else {
	// 服务端简单日志记录
	const serverLog = (type, message, data) => console.log(`[Server][${type}]`, message, data)
	Vue.prototype.$logEvent = (event, params) => serverLog('Event', event, params)
	Vue.prototype.$eventrack = (msg, method, map) => serverLog('Track', msg, { method, ...map })
	Vue.prototype.$adClick = (msg) => serverLog('AdClick', msg)
	Vue.prototype.$adPosition = (msg) => serverLog('AdPosition', msg)
	Vue.prototype.$statsEvent = (eventName, extraParams) => serverLog('StatsEvent', eventName, extraParams)
}

export default function createApp() {
	const router = createRouter()
	const store = createStore()
	const app = new Vue({
		router,
		store,
		render: (h) => h(App),
	})
	return {
		app,
		router,
		store,
	}
}
